/**
 * Column catalogue for the All Deployments table. Data only (no JSX) so it can
 * be unit-tested and shared by the header, the cells and the column toggle.
 * `sortKey` is the server-side `?ordering=` field (contract section 4.7).
 */

export type ColumnId =
  | 'id'
  | 'name'
  | 'customer'
  | 'environment'
  | 'products'
  | 'status'
  | 'region'
  | 'created'
  | 'wwNumber'
  | 'provisioning'
  | 'appServer'
  | 'dbMode'
  | 'dbServer'
  | 'gatewayMode'
  | 'gatewayServer'
  | 'hostname'
  | 'vmCount'
  | 'version'
  | 'terraform'
  | 'cost'
  | 'owner'
  | 'createdBy'
  | 'resourceGroup'
  | 'portal'

export interface ColumnDef {
  id: ColumnId
  label: string
  /** Backend ordering field; absent when the column cannot be sorted. */
  sortKey?: string
  defaultVisible: boolean
  /** "existing" columns were there before the WorkWise round. */
  group: 'existing' | 'workwise'
}

export const COLUMNS: ColumnDef[] = [
  { id: 'id', label: 'ID', defaultVisible: true, group: 'existing' },
  { id: 'name', label: 'Name', defaultVisible: true, group: 'existing' },
  { id: 'customer', label: 'Customer / Tenant', sortKey: 'customer_name', defaultVisible: true, group: 'existing' },
  { id: 'environment', label: 'Environment', sortKey: 'environment', defaultVisible: true, group: 'existing' },
  { id: 'products', label: 'Products', defaultVisible: true, group: 'existing' },
  { id: 'wwNumber', label: 'Cust #', sortKey: 'workwise__customer_number', defaultVisible: true, group: 'workwise' },
  { id: 'dbMode', label: 'DB mode', defaultVisible: true, group: 'workwise' },
  { id: 'gatewayMode', label: 'Gateway mode', defaultVisible: true, group: 'workwise' },
  { id: 'vmCount', label: 'VMs', defaultVisible: true, group: 'workwise' },
  { id: 'status', label: 'Status', sortKey: 'status', defaultVisible: true, group: 'existing' },
  { id: 'provisioning', label: 'Provisioning', defaultVisible: true, group: 'workwise' },
  { id: 'cost', label: 'Est. cost / mo', sortKey: 'workwise__est_monthly_cost', defaultVisible: true, group: 'workwise' },
  { id: 'region', label: 'Region', sortKey: 'azure_region', defaultVisible: true, group: 'existing' },
  { id: 'created', label: 'Created', sortKey: 'created_at', defaultVisible: true, group: 'existing' },
  { id: 'appServer', label: 'App server', defaultVisible: false, group: 'workwise' },
  { id: 'dbServer', label: 'DB server', defaultVisible: false, group: 'workwise' },
  { id: 'gatewayServer', label: 'Gateway server', defaultVisible: false, group: 'workwise' },
  { id: 'hostname', label: 'Host name', defaultVisible: false, group: 'workwise' },
  { id: 'version', label: 'Version', defaultVisible: false, group: 'workwise' },
  { id: 'terraform', label: 'Terraform', defaultVisible: false, group: 'workwise' },
  { id: 'owner', label: 'Owner', defaultVisible: false, group: 'workwise' },
  { id: 'createdBy', label: 'Created by', defaultVisible: false, group: 'workwise' },
  { id: 'resourceGroup', label: 'Resource group', defaultVisible: false, group: 'workwise' },
  { id: 'portal', label: 'Azure portal', defaultVisible: false, group: 'workwise' },
]

export const COLUMN_IDS: ColumnId[] = COLUMNS.map((c) => c.id)
export const DEFAULT_VISIBLE_COLUMNS: ColumnId[] = COLUMNS.filter((c) => c.defaultVisible).map((c) => c.id)

export function columnById(id: string): ColumnDef | undefined {
  return COLUMNS.find((c) => c.id === id)
}

// ─── Sorting ─────────────────────────────────────────────────────────────────

export type SortDirection = 'asc' | 'desc'

/** "-created_at" -> { key: "created_at", direction: "desc" }; "" -> null. */
export function parseOrdering(ordering: string): { key: string; direction: SortDirection } | null {
  const first = ordering.split(',')[0]?.trim()
  if (!first) return null
  return first.startsWith('-') ? { key: first.slice(1), direction: 'desc' } : { key: first, direction: 'asc' }
}

/**
 * Click cycle for a sortable header: unsorted -> ascending -> descending ->
 * unsorted (the server default).
 */
export function nextOrdering(current: string, sortKey: string): string {
  const parsed = parseOrdering(current)
  if (!parsed || parsed.key !== sortKey) return sortKey
  return parsed.direction === 'asc' ? `-${sortKey}` : ''
}
